/**
 * Custom hooks for site content sourced from local YAML files.
 *
 * Content files:
 * - content/site/announcement/index.yaml - Global announcement bar
 * - content/site/banners/index.yaml - Page-specific banners
 * - content/site/categories/index.yaml - Blog and event categories
 * - content/site/footer/index.yaml - Footer content (social, address, text)
 * - content/site/navigation/navigation.yaml - Header and footer navigation menus
 * - content/site/news/index.yaml - News/press mentions for About page
 * - content/site/seo/index.yaml - Global SEO defaults (title, description, social image)
 * - content/site/features/index.yaml - Feature flags for progressive rollout
 *
 * To update content, edit the YAML files directly. Changes take effect on rebuild.
 */

import { useStaticQuery, graphql } from "gatsby"

/**
 * Internal hook that fetches all site content from local YAML files.
 */
function useSiteContentData() {
  return useStaticQuery(graphql`
    query SiteContentQuery {
      announcementYaml {
        announcementId
        text
        ctaText
        ctaLink
        enabled
      }
      bannersYaml {
        banners {
          path
          bannerId
          bannerText
          bannerLink
          enabled
        }
      }
      categoriesYaml {
        blog
        events
        tutorials
      }
      footerYaml {
        social {
          twitter {
            url
            handle
          }
          linkedin {
            url
          }
          youtube {
            url
          }
          instagram {
            url
          }
          facebook {
            url
          }
          github {
            url
          }
        }
        companyAddress {
          line1
          line2
        }
        footerText
      }
      navigationYaml {
        header {
          label
          path
          position
          items {
            label
            path
            description
            icon
          }
        }
        footerPrimaryMenu {
          label
          path
          items {
            label
            path
          }
        }
        footerSecondaryMenu {
          label
          path
        }
      }
      seoYaml {
        siteTitle
        defaultDescription
        socialPreviewImage
      }
      featuresYaml {
        enableTutorialsRedirect
      }
      newsYaml {
        news {
          title
          url
          thumbnailImage {
            childImageSharp {
              gatsbyImageData(quality: 100)
            }
          }
        }
      }
    }
  `)
}

/**
 * Get the global announcement bar content.
 *
 * @returns {Object|null} Announcement bar data: { id, text, ctaText, ctaLink } or null if disabled
 */
export function useAnnouncement() {
  const data = useSiteContentData()
  const announcement = data.announcementYaml

  if (announcement?.enabled && announcement?.text) {
    return {
      id: announcement.announcementId,
      text: announcement.text,
      ctaText: announcement.ctaText,
      ctaLink: announcement.ctaLink,
    }
  }

  return null
}

/**
 * Get all enabled page-specific banners.
 *
 * @returns {Array} Array of banner objects: [{ path, bannerId, bannerText, bannerLink }]
 */
export function useBanners() {
  const data = useSiteContentData()
  const banners = data.bannersYaml?.banners || []

  return banners.filter((b) => b.enabled !== false)
}

/**
 * Get a banner for a specific page path.
 *
 * @param {string} pagePath - The page path to find a banner for
 * @returns {Object|null} Banner data or null if no banner for this path
 */
export function useBannerForPath(pagePath) {
  const banners = useBanners()
  return banners.find((b) => b.path === pagePath) || null
}

/**
 * Get blog categories for filtering.
 *
 * @returns {Array} Array of category strings
 */
export function useBlogCategories() {
  const data = useSiteContentData()
  return data.categoriesYaml?.blog || []
}

/**
 * Get event categories for filtering.
 *
 * @returns {Array} Array of category strings
 */
export function useEventCategories() {
  const data = useSiteContentData()
  return data.categoriesYaml?.events || []
}

/**
 * Get tutorial categories for filtering, in the display order configured in
 * content/site/categories/index.yaml.
 *
 * @returns {Array} Array of category strings
 */
export function useTutorialCategories() {
  const data = useSiteContentData()
  return data.categoriesYaml?.tutorials || []
}

/**
 * Get social media links.
 *
 * @returns {Object} Social links: { twitter, linkedin, youtube, github }
 */
export function useSocialLinks() {
  const data = useSiteContentData()
  return data.footerYaml?.social || {}
}

/**
 * Get company address.
 *
 * @returns {Object} Address: { line1, line2 }
 */
export function useCompanyAddress() {
  const data = useSiteContentData()
  return data.footerYaml?.companyAddress || {}
}

/**
 * Get footer text (copyright/legal).
 *
 * @returns {String} Footer text in markdown format
 */
export function useFooterText() {
  const data = useSiteContentData()
  return data.footerYaml?.footerText || ""
}

/**
 * Get all footer content at once.
 *
 * @returns {Object} { social, companyAddress, footerText }
 */
export function useFooterContent() {
  const data = useSiteContentData()
  return {
    social: data.footerYaml?.social || {},
    companyAddress: data.footerYaml?.companyAddress || {},
    footerText: data.footerYaml?.footerText || "",
  }
}

/**
 * Get header navigation menu items.
 *
 * @returns {Array} Array of navigation sections with items
 */
export function useHeaderNavigation() {
  const data = useSiteContentData()
  return data.navigationYaml?.header || []
}

/**
 * Get footer primary menu (column navigation).
 *
 * @returns {Array} Array of footer menu sections with items
 */
export function useFooterPrimaryMenu() {
  const data = useSiteContentData()
  return data.navigationYaml?.footerPrimaryMenu || []
}

/**
 * Get footer secondary menu (bottom links like Terms, Privacy).
 *
 * @returns {Array} Array of footer menu items
 */
export function useFooterSecondaryMenu() {
  const data = useSiteContentData()
  return data.navigationYaml?.footerSecondaryMenu || []
}

/**
 * Get global SEO defaults.
 *
 * @returns {Object} { siteTitle, defaultDescription, socialPreviewImage }
 */
export function useGlobalSEO() {
  const data = useSiteContentData()
  return {
    siteTitle: data.seoYaml?.siteTitle || "Preset",
    defaultDescription: data.seoYaml?.defaultDescription || "",
    socialPreviewImage: data.seoYaml?.socialPreviewImage || "",
  }
}

/**
 * Get feature flags for progressive rollout.
 *
 * @returns {Object} Feature flags: { enableTutorialsRedirect }
 */
export function useFeatureFlags() {
  const data = useSiteContentData()
  return {
    enableTutorialsRedirect:
      data.featuresYaml?.enableTutorialsRedirect ?? false,
  }
}

/**
 * Get news/press mentions for the About page.
 *
 * @returns {Array} Array of news items: [{ title, url, thumbnail }]
 */
export function useNews() {
  const data = useSiteContentData()
  return (data.newsYaml?.news || []).map((item) => ({
    title: item.title,
    url: item.url,
    thumbnail: item.thumbnailImage?.childImageSharp?.gatsbyImageData
      ? { gatsbyImageData: item.thumbnailImage.childImageSharp.gatsbyImageData }
      : null,
  }))
}
